import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { IFButton, IFTable, IFText, IFSkeleton } from 'Components'
import { Colors } from 'Theme'
import Styles from './TariffList.module.css'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import ReportIcon from '@mui/icons-material/Report'
import moment from 'moment'
import TariffSelectors from 'Stores/Tariff/Selectors'
import TariffActions from 'Stores/Tariff/Actions'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import TariffDetailsContainer from 'Containers/TariffDetailsContainer/TariffDetailsContainer'
import { Popover } from '@mui/material'
import { Box } from '@mui/system'

const TariffList = ({
  tariffs,
  tariffsPaginationOffset,
  tariffFilter,
  fetchTariffListRequestState,
  fetchTariffList,
  tablesHeight,
}) => {
  const { t } = useTranslation()
  const [copiedRowIndex, setCopiedRowIndex] = useState(null)
  const [hoveredRowId, setHoveredRowId] = useState(null)
  const tableRef = useRef()
  const [tariffIdFromUrl, setTariffIdFromUrl] = useState(null)
  useEffect(() => {
    const slashArray = window.location.pathname.split('/')
    const id = slashArray[slashArray.length - 1]

    if (id && id !== 'tariffs') {
      setTariffIdFromUrl(id)
    }
  }, [])

  useEffect(() => {
    if (
      fetchTariffListRequestState !== RequestState.SUCCEEDED ||
      !tableRef.current ||
      !tariffIdFromUrl ||
      !tariffs.length
    ) {
      return
    }
    const rowData = tariffs.find((t) => t._id === tariffIdFromUrl)
    if (!rowData) return
    tableRef.current.setExpandedRowExternal(rowData._id)
  }, [fetchTariffListRequestState, tariffs, tariffIdFromUrl])

  const loadMoreData = () => {
    if (fetchTariffListRequestState === RequestState.LOADING_MORE) return
    fetchTariffList(tariffFilter, tariffsPaginationOffset)
  }

  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchTariffListRequestState === RequestState.ERROR_0_NETWORK ||
      fetchTariffListRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchTariffListRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchTariffListRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchTariffListRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchTariffListRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    ) {
      setFailedRequest(true)
    }
    if (fetchTariffListRequestState === RequestState.SUCCEEDED) {
      setFailedRequest(false)
    }
  }, [fetchTariffListRequestState])
  const handleRetry = () => {
    fetchTariffList(tariffFilter, tariffsPaginationOffset)
  }
  const animateUid = (rowIndex) => {
    setCopiedRowIndex(rowIndex)
    setTimeout(() => {
      setCopiedRowIndex(null)
    }, 2000)
  }

  const [anchorEl, setAnchorEl] = useState(null)
  const [popoverContent, setPopoverContent] = useState('')
  const handlePopoverOpen = (event, row) => {
    const { name } = row.original
    const textOverFlow = event.currentTarget.querySelector(
      `.${Styles.TableText}`,
    )
    const isOverflowing = (element) =>
      element && element.scrollWidth > element.clientWidth

    if (isOverflowing(textOverFlow)) {
      setAnchorEl(event.currentTarget)
      setPopoverContent({ name })
    }
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  const tariffTableColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        enableEditing: false,
        size: 200,
        header:
          fetchTariffListRequestState === RequestState.LOADING ||
          tariffsPaginationOffset === 0 ? (
            <div className={Styles.SkeletonDiv}>
              <IFSkeleton
                variant="text"
                width="110px"
                animation="none"
                height={20}
                className={Styles.Skeleton}
              />
            </div>
          ) : (
            <div>{t('TariffList.Name')}</div>
          ),
        Cell: ({ row }) => {
          return row.index < tariffs.length ? (
            <div className={Styles.ColumnTitle}>
              <ExpandMoreIcon
                className={Styles.ExpandMoreIcon}
                style={{
                  transform: row.getIsExpanded()
                    ? 'rotate(0deg)'
                    : 'rotate(-90deg)',
                }}
              />
              <div
                className={Styles.ColumnTitle}
                onMouseEnter={(e) => handlePopoverOpen(e, row)}
                onMouseLeave={handlePopoverClose}
              >
                {row.original.name ? (
                  <>
                    <IFText className={Styles.TableText}>
                      {row.original.name}
                    </IFText>
                  </>
                ) : (
                  <IFText>{t('TariffList.NotSet')}</IFText>
                )}
              </div>
            </div>
          ) : failedRequest ? (
            <div className={Styles.IconDiv}>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'uuid',
        size: 200,
        enableEditing: false,
        header:
          fetchTariffListRequestState === RequestState.LOADING ||
          tariffsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('TariffList.UUID')}</IFText>
          ),
        Cell: ({ row }) => {
          const isThisRowCopied = copiedRowIndex === row.index
          return row.index < tariffs.length ? (
            <div
              className={Styles.UUIDContainer}
              onMouseEnter={() => setHoveredRowId(row.index)}
              onMouseLeave={() => setHoveredRowId(null)}
              onClick={(e) => e.stopPropagation()}
            >
              <IFText className={Styles.TableText}>{row.original.uuid}</IFText>

              <div className={Styles.UidCopyContainer}>
                {hoveredRowId === row.index ? (
                  <div
                    className={Styles.iconContainer}
                    onClick={() => {
                      navigator.clipboard.writeText(row.original.uuid)
                      animateUid(row.index)
                    }}
                  >
                    <CheckIcon
                      className={isThisRowCopied ? Styles.visible : Styles.hide}
                      style={{
                        color: Colors.primary,
                      }}
                    />
                    <ContentCopyIcon
                      style={{ color: Colors.ClipboardCopy }}
                      className={
                        !isThisRowCopied ? Styles.visible : Styles.hide
                      }
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'minPrice',
        enableEditing: false,
        header:
          fetchTariffListRequestState === RequestState.LOADING ||
          tariffsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <div>{t('TariffList.MinPrice')}</div>
          ),

        Cell: ({ row }) => {
          return row.index < tariffs.length ? (
            <div className={Styles.AdminColumnCreatedOn}>
              {row.original.minPrice ? (
                <>
                  <IFText className={Styles.TableText}>
                    {row.original.minPrice.inclVat}
                  </IFText>
                </>
              ) : (
                <IFText>{t('TariffList.NotSet')}</IFText>
              )}
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'maxPrice',
        enableEditing: false,

        header:
          fetchTariffListRequestState === RequestState.LOADING ||
          tariffsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <div>{t('TariffList.MaxPrice')}</div>
          ),

        Cell: ({ row }) => {
          return row.index < tariffs.length ? (
            <div className={Styles.AdminColumnCreatedOn}>
              {row.original.maxPrice ? (
                <>
                  <IFText className={Styles.TableText}>
                    {row.original.maxPrice.inclVat}
                  </IFText>
                </>
              ) : (
                <IFText>{t('TariffList.NotSet')}</IFText>
              )}
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'type',
        enableEditing: false,
        header:
          fetchTariffListRequestState === RequestState.LOADING ||
          tariffsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <div>{t('TariffList.Type')}</div>
          ),

        Cell: ({ row }) => {
          return row.index < tariffs.length ? (
            <div className={Styles.AdminColumnCreatedOn}>
              <IFText className={Styles.TableText}>{row.original.type}</IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'startDate',
        enableEditing: false,
        size: 200,
        header:
          fetchTariffListRequestState === RequestState.LOADING ||
          tariffsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <div>{t('TariffList.StartDate')}</div>
          ),

        Cell: ({ row }) => {
          return row.index < tariffs.length ? (
            <div>
              {row.original.startDate ? (
                <>
                  <IFText className={Styles.TableText}>
                    {moment(row.original.startDate).format('MMM DD, YYYY ')}
                  </IFText>
                </>
              ) : (
                <IFText>{t('TariffList.NotSet')}</IFText>
              )}
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'expiryDate',
        enableEditing: false,
        size: 200,
        header:
          fetchTariffListRequestState === RequestState.LOADING ||
          tariffsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <div>{t('TariffList.ExpiryDate')}</div>
          ),

        Cell: ({ row }) => {
          return row.index < tariffs.length ? (
            <div>
              {row.original.expiryDate ? (
                <>
                  <IFText className={Styles.TableText}>
                    {moment(row.original.expiryDate).format('MMM DD, YYYY ')}
                  </IFText>
                </>
              ) : (
                <IFText>{t('TariffList.NotSet')}</IFText>
              )}
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'elements',
        header:
          fetchTariffListRequestState === RequestState.LOADING ||
          tariffsPaginationOffset === 0 ? (
            <IFSkeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('TariffList.Elements')}</IFText>
          ),

        Cell: ({ row }) => {
          return row.index < tariffs.length ? (
            <div>
              <IFText className={Styles.TableText}>
                {row.original.elements.length}
              </IFText>
            </div>
          ) : failedRequest ? (
            <>
              <IFButton
                size="sm"
                color={Colors.red}
                text={t('IFTable.TryAgain')}
                onClick={handleRetry}
              />
            </>
          ) : (
            <IFSkeleton
              variant="text"
              width="60%"
              animation="none"
              height={20}
            />
          )
        },
      },
    ],
    [
      tariffs,
      fetchTariffList,
      fetchTariffListRequestState,
      hoveredRowId,
      copiedRowIndex,
      failedRequest,
    ],
  )
  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )
  useEffect(() => {
    if (
      fetchTariffListRequestState === RequestState.LOADING &&
      tableRef.current
    ) {
      tableRef.current.resetExpandedRows()
    }
  }, [fetchTariffListRequestState, tariffFilter])

  const renderDetailPanel = ({ row }) => {
    if (!row || !row.original) return null
    return <TariffDetailsContainer row={row} />
  }

  return (
    <div>
      {((fetchTariffListRequestState !== RequestState.SUCCEEDED &&
        !tariffsPaginationOffset) ||
        tariffs) && (
        <>
          <IFTable
            ref={tableRef}
            columns={tariffTableColumns}
            data={tariffs}
            fetchMoreOnBottomReached={fetchMoreOnBottomReached}
            paginationOffset={tariffsPaginationOffset}
            filters={tariffFilter}
            showSkeleton={
              fetchTariffListRequestState === RequestState.LOADING ||
              (fetchTariffListRequestState !== RequestState.SUCCEEDED &&
                tariffsPaginationOffset === 0)
            }
            tableMaxHeight={tablesHeight}
            tableSetHeight={'100%'}
            tableRequestState={fetchTariffListRequestState}
            renderDetailPanel={renderDetailPanel}
          />
        </>
      )}
      <Popover
        id="mouse-over-popover"
        className={Styles.Popover}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
      >
        <Box
          className={Styles.ParentBox}
          sx={{
            p: 2,
          }}
        >
          <Box className={Styles.ChildBox}>
            <IFText style={{ color: Colors.DetailPanel }}>
              <strong>{t('TariffList.Name')}</strong>
            </IFText>
            <IFText
              className={Styles.PopoverText}
              style={{
                color: Colors.text,
              }}
            >
              {popoverContent.name}
            </IFText>
          </Box>
        </Box>
      </Popover>
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchTariffList: (filter, offset) =>
      dispatch(TariffActions.fetchTariffList(filter, offset)),
  }
}
const mapStateToProps = (state) => ({
  fetchTariffListRequestState:
    TariffSelectors.getFetchTariffListRequestState(state),
  tariffs: TariffSelectors.getTariffList(state),
  tariffsPaginationOffset: TariffSelectors.getTariffsPaginationOffset(state),
  tariffFilter: TariffSelectors.getTariffFilter(state),
})
TariffList.propTypes = {
  fetchTariffList: PropTypes.func,
  tablesHeight: PropTypes.number,
  tariffsPaginationOffset: PropTypes.number,
  tariffFilter: PropTypes.object,
  fetchTariffListRequestState: PropTypes.number,
  tariffs: PropTypes.array,
}
export default connect(mapStateToProps, mapDispatchToProps)(TariffList)
